import { useTranslation } from "react-i18next";

import {
  RecordDescription,
  RecordMetadata,
  RecordRow,
  RecordTitle,
} from "@/components/record-link";
import { cn } from "@/lib/utils";
import { locationRequestPath } from "@/routes";
import type { Enquiry } from "./types";

function displayString(value: unknown): string {
  return typeof value === "string" && value.trim() ? value : "";
}

function Email({ value }: { value: string }) {
  const at = value.lastIndexOf("@");
  return at > 0 ? (
    <>
      {value.slice(0, at)}
      <wbr />
      {value.slice(at)}
    </>
  ) : (
    value
  );
}

export function RequestCell({
  enquiry,
  locationId,
}: {
  enquiry: Enquiry;
  locationId: string;
}) {
  const { t } = useTranslation();
  const identity =
    displayString(enquiry.customer?.name) ||
    displayString(enquiry.intake_details.name) ||
    (enquiry.channel === "whatsapp"
      ? t("whatsapp.enquiry")
      : enquiry.channel === "instagram"
        ? t("instagram.enquiry")
        : displayString(enquiry.channel_identity)) ||
    t("requests.unnamed");
  const contact = enquiry.customer || enquiry.intake_details;
  const email = displayString(contact.email);
  const phone = displayString(contact.phone);
  const vehicle = enquiry.vehicle || enquiry.intake_details;
  const vehicleName = [vehicle.make, vehicle.model]
    .map(displayString)
    .filter(Boolean)
    .join(" ");
  const registration = displayString(vehicle.registration_number);
  const titleId = `request-title-${enquiry.id}`;

  return (
    <RecordRow
      href={locationRequestPath(locationId, enquiry.id)}
      aria-labelledby={titleId}
      className="request-ledger-row"
    >
      <div className="request-ledger-content">
        <div className="request-ledger-person">
          <span className="request-ledger-local-label">
            {t("requests.cell.customer")}
          </span>
          <RecordTitle id={titleId} className="request-ledger-title">
            {identity}
          </RecordTitle>
        </div>
        <div className="request-ledger-contact">
          <span className="request-ledger-local-label">
            {t("requests.cell.contact")}
          </span>
          {email && (
            <RecordMetadata>
              <Email value={email} />
            </RecordMetadata>
          )}
          {phone && <RecordMetadata>{phone}</RecordMetadata>}
          {!email && !phone && (
            <RecordMetadata>{t("requests.cell.no_contact")}</RecordMetadata>
          )}
        </div>
        <div className="request-ledger-vehicle">
          <span className="request-ledger-local-label">
            {t("requests.cell.vehicle")}
          </span>
          {vehicleName || registration ? (
            <>
              {vehicleName && (
                <RecordDescription>{vehicleName}</RecordDescription>
              )}
              <RecordMetadata>
                {registration || t("requests.cell.no_registration")}
              </RecordMetadata>
            </>
          ) : (
            <RecordMetadata>{t("requests.cell.no_vehicle")}</RecordMetadata>
          )}
        </div>
        <div className="request-ledger-request">
          <span className="request-ledger-local-label">
            {t("requests.cell.request")}
          </span>
          <RecordDescription className="request-ledger-message">
            {enquiry.body}
          </RecordDescription>
        </div>
        <div className="request-ledger-state">
          <span className="sr-only">{t("requests.state")}</span>
          <span>{t(`requests.states.${enquiry.state}`)}</span>
        </div>
        <div className="request-ledger-context">
          <RecordMetadata
            className={cn(!enquiry.complete && "request-ledger-incomplete")}
          >
            {t(enquiry.complete ? "requests.complete" : "requests.incomplete")}
          </RecordMetadata>
          <RecordMetadata>
            {t(`requests.channels.${enquiry.channel}`)}
          </RecordMetadata>
        </div>
      </div>
    </RecordRow>
  );
}

export function RequestLedger({
  enquiries,
  locationId,
}: {
  enquiries: Enquiry[];
  locationId: string;
}) {
  const { t } = useTranslation();
  return (
    <div className="request-ledger">
      <div className="request-ledger-heading" aria-hidden="true">
        <div className="request-ledger-heading-grid">
          <span>{t("requests.cell.customer_contact")}</span>
          <span>{t("requests.cell.vehicle")}</span>
          <span>{t("requests.cell.request")}</span>
          <span>{t("requests.state")}</span>
        </div>
        <span className="request-ledger-chevron-space" />
      </div>
      <ul className="request-ledger-list">
        {enquiries.map((enquiry) => (
          <li key={enquiry.id}>
            <RequestCell enquiry={enquiry} locationId={locationId} />
          </li>
        ))}
      </ul>
    </div>
  );
}
